import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useMutation, useQuery } from '@apollo/client';
import { Warning } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useModalStatusMessage } from '@/hooks';
import { FC, useEffect, useState } from 'react';
import HARD_DELETE_USER from '../graphql/mutations/hardDeleteUser';
import GET_HARD_DELETE_USER_PREVIEW from '../graphql/queries/getHardDeleteUserPreview';

interface Props {
  open: boolean;
  onClose: () => void;
  userId: number | null;
  userFullName?: string;
}

export const HardDeleteUserModal: FC<Props> = ({ userId, userFullName, open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {userId != null && (
        <SuspenseComponent userId={userId} userFullName={userFullName} onClose={onClose} open={open} />
      )}
    </Dialog>
  );
};

const SuspenseComponent: FC<{ userId: number; userFullName?: string; onClose: () => void; open: boolean }> = ({
  userId,
  userFullName,
  onClose,
  open,
}) => {
  const { i18n } = useLingui();
  const [countdown, setCountdown] = useState(0);
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();

  useEffect(() => {
    if (open) {
      setCountdown(3);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [open]);

  const { data: previewData, loading: previewLoading } = useQuery(GET_HARD_DELETE_USER_PREVIEW, {
    variables: { userId },
    fetchPolicy: 'network-only',
  });

  const [hardDeleteUser, { loading: deleteLoading }] = useMutation(HARD_DELETE_USER, {
    variables: { input: { userId } },
    onCompleted: () => {
      openModalSuccessMessage(t(i18n)`Usuario eliminado definitivamente.`);
      onClose();
    },
    onError: (error) => {
      openModalErrorMessage(error.message);
    },
    refetchQueries: ['GetUsersGraveyard'],
  });

  const preview = previewData?.hardDeleteUserPreview;
  const deletedTables = preview?.affectedTables.filter((t) => !t.preserved) ?? [];
  const preservedTables = preview?.affectedTables.filter((t) => t.preserved) ?? [];

  return (
    <>
      <ModalTitle
        title={
          <Trans>
            Eliminar definitivamente{userFullName && <> <strong>{userFullName}</strong></>}
          </Trans>
        }
      />
      <ModalContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="error" />
            <Typography fontWeight="bold" color="error">
              <Trans>Esta acción es IRREVERSIBLE</Trans>
            </Typography>
          </Box>
          <Typography>
            <Trans>
              Se eliminarán permanentemente el usuario y todos sus datos asociados. No podrá
              recuperarse esta información.
            </Trans>
          </Typography>
          <Typography color="text.secondary" fontSize="small">
            <Trans>El correo electrónico quedará disponible para ser reutilizado.</Trans>
          </Typography>

          {previewLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : preview ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <Trans>Registros que se eliminarán:</Trans>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Trans>Tabla</Trans>
                    </TableCell>
                    <TableCell align="right">
                      <Trans>Registros</Trans>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedTables.map((row) => (
                    <TableRow key={row.table}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {row.table}
                      </TableCell>
                      <TableCell align="right">{row.count >= 0 ? row.count : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {preservedTables.map((row) => (
                    <TableRow key={row.table} sx={{ opacity: 0.6 }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {row.table}
                        <Chip
                          label={t(i18n)`conservado`}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1, fontSize: '0.65rem', height: 16 }}
                        />
                      </TableCell>
                      <TableCell align="right">—</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Box>
      </ModalContent>
      <ModalActions
        onClose={onClose}
        submitButton={{
          label: countdown > 0
            ? t(i18n)`Eliminar definitivamente (${countdown}s)`
            : t(i18n)`Eliminar definitivamente`,
          onClick: () => hardDeleteUser(),
          loading: deleteLoading,
          disabled: previewLoading || countdown > 0,
          color: 'error',
        }}
      />
    </>
  );
};
